import IconRoleEditorial from "@/components/common/Icons/IconRoleEditorial";
import IconRoleJournalist from "@/components/common/Icons/IconRoleJournalist";
import IconRolePress from "@/components/common/Icons/IconRolePress";
import { useToastPromise } from "@/hooks/useToast";
import {
  Box,
  Button,
  Flex,
  Icon,
  SimpleGrid,
  Link as StyledLink,
  Text,
} from "@chakra-ui/react";
import axios from "axios";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { LogIn, UserPlus } from "react-feather";
import { FormProvider, useForm } from "react-hook-form";
import Fade from "react-reveal/Fade";
import EditorialOfficeForm from "./EditorialOfficeForm";
import PressOfficeForm from "./PressOfficeForm";
import Role from "./Role";
import UserForm from "./UserForm";

enum FormType {
  User = "user",
  PressOffice = "press",
  EditorialOffice = "editorial",
}

const postFormTypeUrl = {
  [FormType.User]: "/user",
  [FormType.PressOffice]: "/press-office",
  [FormType.EditorialOffice]: "/editorial-office",
};

type Props = {
  token?: string;
  office?: any;
};

const Signup = ({ token, office }: Props): React.ReactElement => {
  const [maxFormSteps, setMaxFormSteps] = useState(0);
  const [formStep, setFormStep] = useState(0);
  const [formType, setFormType] = useState<FormType>();
  const methods = useForm();
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    trigger,
    setError,
    watch,
  } = methods;
  const { t } = useTranslation("common");
  const { locale } = useRouter();
  const toast = useToastPromise();
  const [accountCreateSuccess, setAccountCreateSuccess] = useState(false);
  const [chooseSignupOrSignin, setChooseSignupOrSignin] = useState(false);
  const [
    chooseSignupOrSigninForEditorial,
    setChooseSignupOrSigninForEditorial,
  ] = useState(false);
  const formState = watch();

  useEffect(() => {
    // Show only user form if invited by email
    if (token) {
      setChooseSignupOrSignin(true);
    } else if (office) {
      setChooseSignupOrSigninForEditorial(true);
      setFormType(FormType.EditorialOffice);
      setMaxFormSteps(2);
      setFormStep(1);
      reset({
        id: office.id,
        name: office.name,
        representative: office.representative,
        url: office.url,
        officeEmail: office.email,
        phone: office.phone,
        address: office.address,
        city: office.city,
        postalCode: office.postalCode,
        country: office.country,
        type: office.type,
      });
    }
  }, []);

  const onSubmit = (data) => {
    return toast.promise(
      axios
        .post(postFormTypeUrl[formType], { ...data, token, locale })
        .then(() => {
          reset();
          setAccountCreateSuccess(true);
        })
        .catch((error) => {
          if (error == "Email taken")
            setError(
              "email",
              {
                type: "focus",
                message: t("users.emailTaken"),
              },
              { shouldFocus: true }
            );
          else if (error === "Name taken") {
            setFormStep((cur) => cur - 1);
            setError(
              "name",
              {
                type: "focus",
                message: t("errorKeys.nameTaken", { context: formType }),
              },
              { shouldFocus: true }
            );
          }

          throw error;
        }),
      {
        success: t("users.accountCreated"),
        errorKeys: {
          "Email taken": t("users.emailTaken"),
          "Name taken": t("errorKeys.nameTaken", { context: formType }),
        },
      }
    );
  };

  const handleFormTypeChange = (type: FormType) => {
    setFormType(type);
    setFormStep((cur) => cur + 1);
    switch (type) {
      case FormType.User:
        setMaxFormSteps(1);
        break;
      default:
        setMaxFormSteps(2);
    }
  };

  const handleStepCompletion = async () => {
    if (await trigger()) {
      setFormStep((cur) => cur + 1);
    }
  };

  const handleGoBackToPreviousStep = () => {
    if (formStep == 2 && office && !chooseSignupOrSigninForEditorial) {
      setChooseSignupOrSigninForEditorial(true);
    } else if (formStep == 2 && office && chooseSignupOrSigninForEditorial) {
      setFormType(FormType.EditorialOffice);
      setFormStep((cur) => cur - 1);
    } else {
      setFormStep((cur) => cur - 1);
    }
  };

  return (
    <>
      {!accountCreateSuccess ? (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {formStep === 0 && !chooseSignupOrSignin && (
              <SimpleGrid columns={1} spacingX={3} spacingY="30px">
                <Role
                  name={t("reporter")}
                  description={t("users.roleDescription")}
                  onClick={() => handleFormTypeChange(FormType.User)}
                  icon={<IconRoleJournalist />}
                />
                <Role
                  name={t("pressOffice.pressOffice")}
                  description={t("pressOffice.roleDescription")}
                  onClick={() => handleFormTypeChange(FormType.PressOffice)}
                  icon={<IconRolePress />}
                />
                <Role
                  name={t("editorialOffice.editorialOffice")}
                  description={t("editorialOffice.roleDescription")}
                  onClick={() => handleFormTypeChange(FormType.EditorialOffice)}
                  icon={<IconRoleEditorial />}
                />
              </SimpleGrid>
            )}

            {(chooseSignupOrSignin ||
              (chooseSignupOrSigninForEditorial && formStep == 2)) && (
              <SimpleGrid columns={1} spacingX={3} spacingY="30px" mt={5}>
                <Link
                  href={{
                    pathname: "/signin",
                    query: {
                      token: token,
                      id: formState.id,
                      name: formState.name,
                      representative: formState.representative,
                      url: formState.url,
                      officeEmail: formState.officeEmail,
                      phone: formState.phone,
                      address: formState.address,
                      city: formState.city,
                      postalCode: formState.postalCode,
                      country: formState.country,
                      type: formState.type,
                    },
                  }}
                >
                  <Role
                    name={t("users.signin")}
                    description={
                      token
                        ? t("users.signinFromInviteOfficeAssigment")
                        : t("users.signinFromInviteOfficeAdd")
                    }
                    icon={
                      <Icon as={LogIn} w={12} h={12} />
                    } /* TODO add better signin icon here */
                  />
                </Link>
                <Role
                  name={t("users.signup")}
                  description={t("users.signupFromInvite")}
                  onClick={() => {
                    setChooseSignupOrSignin(false);
                    setChooseSignupOrSigninForEditorial(false);
                    setFormType(FormType.User);
                    if (!office) {
                      setMaxFormSteps(1);
                      setFormStep(1);
                    } else {
                      setFormStep(2);
                    }
                  }}
                  icon={
                    <Icon as={UserPlus} w={12} h={12} />
                  } /* TODO add better signup icon here */
                />
              </SimpleGrid>
            )}
            {formStep >= 1 && !chooseSignupOrSignin && (
              <Box display={formStep === 1 ? "block" : "none"}>
                {formType === FormType.PressOffice && <PressOfficeForm />}
                {formType === FormType.EditorialOffice && (
                  <EditorialOfficeForm />
                )}
              </Box>
            )}
            {((formType === FormType.User &&
              formStep >= 1 &&
              !chooseSignupOrSignin &&
              !office) ||
              (formStep >= 2 &&
                !(
                  chooseSignupOrSignin || chooseSignupOrSigninForEditorial
                ))) && (
              <Box
                display={
                  (formType === FormType.User && formStep === 1) ||
                  formStep === 2
                    ? "block"
                    : "none"
                }
              >
                <UserForm />
              </Box>
            )}
            {formStep >= 1 && !token && !(office && formStep < 2) && (
              <Text textAlign="right" mt="15px">
                <StyledLink onClick={handleGoBackToPreviousStep}>
                  {t("goBack")}
                </StyledLink>
              </Text>
            )}

            {!chooseSignupOrSignin && (
              <Flex mt="2rem" justify="center">
                <Link
                  href={{
                    pathname: "/signin",
                    query: { token },
                  }}
                >
                  <Button variant="outline" size="xl" data-cy="signin-button">
                    {t("users.signin")}
                  </Button>
                </Link>

                {formStep < maxFormSteps && (
                  <Button ml="5" size="xl" onClick={handleStepCompletion}>
                    {t("next")}
                  </Button>
                )}

                {!!maxFormSteps &&
                  formStep === maxFormSteps &&
                  !chooseSignupOrSigninForEditorial && (
                    <Button
                      type="submit"
                      size="xl"
                      ml="5"
                      data-cy="signup-button"
                      isLoading={isSubmitting}
                    >
                      {t("users.signup")}
                    </Button>
                  )}
              </Flex>
            )}
          </form>
        </FormProvider>
      ) : (
        <Fade duration={3000}>
          <SimpleGrid spacingY={5}>
            <Text fontSize={"xl"} lineHeight={8}>
              {t("users.activateAccountEmail")}
            </Text>
          </SimpleGrid>
        </Fade>
      )}
    </>
  );
};

export default Signup;
